import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { QUERY_KEY_CONST } from '@/constant';
import { useTaskDetailQuery } from '@/hook';
import {
  useCollectionManagementStore,
  usePcdCacheStore,
  usePcdFileStore,
  useTaskFrameStore,
} from '@/store';

import ToolBootstrap from './ToolBootstrap';
import ToolInvalidPathRedirector from './ToolInvalidPathRedirector';

type ToolEntryResolverInternalProps = {
  frameSeq: number;
  projectID: number;
  taskID: number;
};

const ToolEntryResolverInternal = ({
  frameSeq,
  projectID,
  taskID,
}: ToolEntryResolverInternalProps) => {
  const queryClient = useQueryClient();
  const { currentBucket, setCurrentBucket } = useCollectionManagementStore();
  const { selectedTask, selectTask, setAllFrameList } = useTaskFrameStore();
  const taskDetailResult = useTaskDetailQuery(taskID);
  const isInitialized = useRef(false);
  const currentBucketForRecovered = useRef(currentBucket);
  const task = taskDetailResult.data;
  const pathParam = {
    frameSeq,
    projectID,
    taskID,
  };

  useEffect(() => {
    if (!task || isInitialized.current) return;

    selectTask(task);
    setAllFrameList([]);
    setCurrentBucket(task.bucket);

    isInitialized.current = true;

    return () => {
      setAllFrameList([]);
    };
  }, [task]);

  useEffect(() => {
    if (!selectedTask) return;

    usePcdFileStore.getState().setPath(selectedTask.pcdPath);
  }, [selectedTask]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: QUERY_KEY_CONST.FRAME_KEYS.getList(),
      });
      usePcdCacheStore.getState().clear();
      usePcdFileStore.getState().clear();
      setCurrentBucket(currentBucketForRecovered.current);
    };
  }, []);

  return selectedTask && <ToolBootstrap {...pathParam} />;
};

const ToolEntryResolver = (pathParam: {
  projectId?: string;
  taskId?: string;
  frameSeq?: string;
}) => {
  const { projectId, taskId, frameSeq = '1' } = pathParam;

  if (projectId == null || taskId == null || frameSeq == null) {
    return <ToolInvalidPathRedirector />;
  }

  return (
    <ToolEntryResolverInternal
      frameSeq={+frameSeq}
      projectID={+projectId}
      taskID={+taskId}
    />
  );
};

export default ToolEntryResolver;
